import { FC } from 'react';
import cx from 'classnames';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import TooltipContainer from '../../../components/shared/TooltipContainer/TooltipContainer';
import DotRange, { DotRangeLegend } from '../../DotRange/DotRange';
import {
  BidRank,
  BidSpread,
  PurchaseBid,
  amountText,
  amountWithUnit,
  percentDifference,
  rankOf,
  spreadOf,
} from './bidsOfPurchase';
import styles from './PurchaseBids.module.scss';

const signedPercent = (percent: number) => `${percent < 0 ? '−' : percent > 0 ? '+' : ''}${Math.abs(percent)} %`;

type ComparisonProps = {
  amount: number;
  reference: number;
  label: string;
  formula: string;
};

const Comparison: FC<ComparisonProps> = ({ amount, reference, label, formula }) => {
  const difference = percentDifference(amount, reference);
  const percent = signedPercent(difference);

  return (
    <div className={styles.difference}>
      <TooltipContainer
        className={styles.calculationTooltip}
        renderReferenceComponent={(className, ref) => (
          <span
            ref={ref}
            className={cx(className, styles.percent, {
              [styles.below]: difference < 0,
              [styles.above]: difference > 0,
            })}
          >
            {percent}
          </span>
        )}
      >
        <div className={styles.calculation}>
          <span>{formula}</span>
          <span className={styles.calculationNumbers}>
            ({amountText(amount)} − {amountText(reference)}) / {amountText(reference)} = {percent}
          </span>
        </div>
      </TooltipContainer>
      {label}
    </div>
  );
};

type TooltipProps = {
  bid: PurchaseBid;
  spread: BidSpread;
  rank: BidRank;
  isOnlyBid: boolean;
  budget?: number;
};

const BidTooltip: FC<TooltipProps> = ({ bid, spread, rank, isOnlyBid, budget }) => {
  const [t] = useTranslation('projects', { keyPrefix: 'purchaseList.bids' });

  const rankLabel = () => {
    if (rank.kind === 'lowest') return t('lowest', 'Lägst');
    if (rank.kind === 'highest') return t('highest', 'Högst');
    return t('rank', '{{position}} av {{count}}', { position: rank.position, count: rank.count });
  };

  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipHeader}>
        <span className={styles.supplierName}>{bid.supplierName}</span>
        {!isOnlyBid && <span className={styles.rank}>{rankLabel()}</span>}
      </div>
      <div className={styles.amount}>{amountWithUnit(bid.amount, spread.unit)}</div>
      {!isOnlyBid && (
        <Comparison
          amount={bid.amount}
          reference={spread.average}
          label={t('comparedToAverage', 'mot snitt')}
          formula={t('calculation.average', '(Anbud − Snitt) / Snitt')}
        />
      )}
      {!!budget && (
        <Comparison
          amount={bid.amount}
          reference={budget}
          label={t('comparedToBudget', 'mot budget')}
          formula={t('calculation.budget', '(Anbud − Budget) / Budget')}
        />
      )}
      {bid.bidDate && (
        <div className={styles.received}>
          {t('received', 'Inkom {{date}}', { date: moment(bid.bidDate).format('D MMM YYYY') })}
        </div>
      )}
    </div>
  );
};

type ChartProps = {
  bids: PurchaseBid[];
  spread: BidSpread;
  budget?: number;
};

const BidChart: FC<ChartProps> = ({ bids, spread, budget }) => {
  const [t] = useTranslation('projects', { keyPrefix: 'purchaseList.bids' });
  const isOnlyBid = bids.length === 1;
  const points = bids.map(bid => ({
    id: bid.supplierId,
    value: bid.amount,
    tooltip: (
      <BidTooltip
        bid={bid}
        spread={spread}
        rank={rankOf(bid.amount, bids)}
        isOnlyBid={isOnlyBid}
        budget={budget}
      />
    ),
  }));

  return (
    <div className={styles.chart}>
      <div className={styles.headline}>
        {isOnlyBid ? t('onlyBid', 'Enda anbud') : t('average', 'Snitt')}{' '}
        <strong>{amountWithUnit(spread.average, spread.unit)}</strong>
      </div>
      <DotRange points={points} marker={isOnlyBid ? undefined : spread.average} />
      {!isOnlyBid && (
        <div className={styles.extremes}>
          <span>{amountText(spread.lowest)}</span>
          <span>{amountText(spread.highest)}</span>
        </div>
      )}
    </div>
  );
};

type Props = {
  bids: PurchaseBid[];
  budget?: number;
};

export const PurchaseBids: FC<Props> = ({ bids, budget }) => {
  if (!bids.length) return null;

  const spread = spreadOf(bids);
  return (
    <div className={styles.container}>
      <span className={styles.count}>{bids.length}</span>
      {spread && <BidChart bids={bids} spread={spread} budget={budget} />}
    </div>
  );
};

export const PurchaseBidsTitle: FC = () => {
  const [t] = useTranslation('projects', { keyPrefix: 'purchaseList' });
  return (
    <span className={styles.title}>
      {t('columns.bids', 'Anbud')}
      <DotRangeLegend
        pointLabel={t('bids.legend.bid', 'anbud')}
        spanLabel={t('bids.legend.spread', 'spann')}
        markerLabel={t('bids.legend.average', 'snitt')}
      />
    </span>
  );
};
